'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { z } from '@/lib/validation/zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useId, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { FormErrorBanner } from '@/components/site/forms/form-error-banner'
import { ReCaptcha } from '@/components/site/forms/register/recaptcha'
import { Button } from '@/components/ui/button'
import { ErrorMessage, Field, Label } from '@/components/ui/fieldset'
import { Input } from '@/components/ui/input'
import { analytics } from '@/lib/analytics/posthog'

import { recaptchaSiteKey } from '@/config/env.public'
import { attributionForSignup } from '@/lib/analytics/attribution'
import { identifyByEmail } from '@/lib/analytics/identity'
import { firePixelConversion } from '@/lib/analytics/pixels'
import { applyApiErrors, requestJson } from './register-helpers'
import type { RegisterCopy, RegisterData, RegisterUi } from './register-types'

type RegisterEmailResponse = Partial<RegisterData> & { eventId?: string; externalId?: string }

type EmailStepProps = {
  locale: string
  copy: RegisterCopy['email']
  ui: RegisterUi
  defaultValues: RegisterData
  onNext: (data: Partial<RegisterData>) => void
}

export function RegisterEmailStep({ locale, copy, ui, defaultValues, onNext }: EmailStepProps) {
  const emailId = useId()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [captchaError, setCaptchaError] = useState<string | null>(null)
  const [captchaResetKey, setCaptchaResetKey] = useState(0)

  const schema = useMemo(
    () =>
      z.object({
        email: z.string().trim().min(1, ui.validation.emailRequired).email(ui.validation.emailInvalid),
      }),
    [ui.validation.emailRequired, ui.validation.emailInvalid],
  )

  const {
    register,
    handleSubmit,
    setFocus,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<{ email: string }>({
    defaultValues: { email: defaultValues.email || '' },
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    setFocus('email')
  }, [setFocus])

  const onSubmit = handleSubmit(async (values) => {
    setErrorMessage(null)

    if (recaptchaSiteKey && !captchaToken) {
      setCaptchaError(ui.validation.captchaRequired)
      return
    }

    try {
      const data = await requestJson<RegisterEmailResponse>('register', {
        method: 'PUT',
        body: JSON.stringify({
          ...defaultValues,
          ...attributionForSignup(),
          email: values.email,
          token: recaptchaSiteKey ? captchaToken : undefined,
        }),
      })

      // Identify the lead by (normalized) email so the anonymous funnel — including the
      // attribution-carrying pageviews — merges into one person that the backend also targets.
      identifyByEmail(values.email)
      analytics.capture('signup_started', { conversion_id: data?.eventId })

      // The backend fires FacebookLead here with eventId `lead.<sha256(email)>`; the browser
      // pixels use the same eventId so the platforms dedup against that server-side CAPI event.
      firePixelConversion('lead', { eventId: data?.eventId, externalId: data?.externalId, email: values.email })

      onNext({ ...values, ...(data ?? {}) })
    } catch (error) {
      applyApiErrors(error, setError, setErrorMessage, ui.validation.submitFailed)
    } finally {
      setCaptchaToken(null)
      setCaptchaResetKey((value) => value + 1)
    }
  })

  const labelClass = 'text-mist-900 dark:text-mist-100'
  const buttonColor = 'dark/zinc'

  return (
    <form className="space-y-5" noValidate onSubmit={onSubmit}>
      {errorMessage && <FormErrorBanner className="font-medium">{errorMessage}</FormErrorBanner>}

      <Field>
        <Label htmlFor={emailId} className={labelClass} required>
          {copy.emailLabel}
        </Label>
        <Input
          id={emailId}
          type="email"
          autoComplete="email"
          placeholder={ui.placeholders.signup.emailStep.email}
          {...register('email')}
          invalid={Boolean(errors.email)}
          aria-invalid={Boolean(errors.email)}
          aria-describedby={errors.email ? `${emailId}-error` : undefined}
          disabled={isSubmitting}
        />
        {errors.email?.message && <ErrorMessage id={`${emailId}-error`}>{errors.email.message}</ErrorMessage>}
      </Field>

      <ReCaptcha
        locale={locale}
        action="REGISTER"
        onChange={(token) => {
          setCaptchaToken(token)
          if (token) {
            setCaptchaError(null)
          }
        }}
        error={captchaError}
        resetKey={captchaResetKey}
        unavailableLabel={ui.validation.captchaUnavailable}
      />

      <div className="pt-2">
        <Button type="submit" size="lg" color={buttonColor} disabled={isSubmitting}>
          {copy.actions.submit}
        </Button>
      </div>
    </form>
  )
}
